'use client';

import React, { useEffect, useRef } from 'react';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import { useWindowSizeIsLessThan } from 'src/hooks/useWindowSize';
import { cm } from 'src/utils/classNames';
import ScrollShadows from '../ScrollShadows';
import {
	TradeTableTabValue,
	useTradePageTableTabs,
} from 'src/components/TradePageTable/hooks/useTradePageTableTabs';
import { OpenOrdersFilters } from '../OpenOrdersTable/OpenOrdersFilters';
import Checkbox from '../primitives/Checkbox/Checkbox';
import { useTableFiltersState } from 'src/hooks/useTableFiltersState';
import Tabs from 'src/components/primitives/Tabs/Tabs';

interface Props {
	activeTab: TradeTableTabValue;
	setActiveTab: (newTab: TradeTableTabValue) => void;
	positionCount: number;
	openOrderCount: number;
	balanceCount: number;
	overviewPage?: boolean;
}

export const TradePageTableTabs = ({
	activeTab,
	setActiveTab,
	positionCount,
	openOrderCount,
	balanceCount,
	overviewPage,
}: Props) => {
	const isMobileScreenSize = useIsMobileScreenSize();
	const isLessThanXL = useWindowSizeIsLessThan('xl');

	const isOnTradePage = !overviewPage;

	const { showOnlyCurrentMarket = false, setShowOnlyCurrentMarket } =
		useTableFiltersState({ overviewPage });

	const tableTabsRef = useRef<HTMLDivElement>(null);

	const tabOptions = useTradePageTableTabs({
		balanceCount,
		positionCount,
		openOrderCount,
		overviewPage,
		activeTab,
	});

	// Select first tab if currently selected tab isn't found. This is possible when
	// changing between pages that have different tab options (e.g. trade vs overview).
	useEffect(() => {
		if (!tabOptions.find((option) => option.value === activeTab)) {
			setActiveTab(tabOptions[0].value);
		}
	}, [tabOptions, setActiveTab, activeTab]);

	const hideOtherMarketsCheckbox = (
		<Checkbox.Default
			id="hide-other-markets"
			label="Hide other markets"
			checked={!!showOnlyCurrentMarket}
			onCheckedChange={() => setShowOnlyCurrentMarket(!showOnlyCurrentMarket)}
		/>
	);

	return (
		<ScrollShadows
			className="w-full"
			scrollableRef={tableTabsRef}
			gradientFromClassName="from-container-bg"
			screenSize="all"
		>
			<div
				id="trade_page_table_tabs"
				className={cm(
					'relative flex w-full items-center justify-between overflow-x-auto overflow-y-hidden',
					'hidden-scroll border-0 border-stroke-divider p-md sm:border-b'
				)}
				ref={tableTabsRef}
			>
				<Tabs
					size={isOnTradePage && isLessThanXL ? 'sm' : 'md'}
					variant="secondary"
					options={tabOptions}
					activeTab={activeTab}
					onTabChange={setActiveTab}
					dataTestId="trade-page-table-tabs"
					tabTestIdPrefix="trade-page-table-tab"
				/>
				<div className="flex items-center gap-x-sm">
					{activeTab === 'positions' && (
						<>
							{isOnTradePage && !isMobileScreenSize && hideOtherMarketsCheckbox}
						</>
					)}
					{activeTab === 'openOrders' && (
						<>
							{!isOnTradePage && (
								<OpenOrdersFilters overviewPage={overviewPage} />
							)}
							{isOnTradePage && !isMobileScreenSize && hideOtherMarketsCheckbox}
						</>
					)}
				</div>
			</div>
		</ScrollShadows>
	);
};
